/**
 * Dashboard — "לוח בקרה" tab of Email Automation: is sending healthy, what's queued, what went out.
 *
 * Views (Segmented switch, state `activeView`):
 *   overview     services (scheduler / pg-boss / queue health), queue, enrollments, rate limits,
 *                failure rate, recent pg-boss activity
 *   scheduled    enrollments due now + scheduled in the next 48h (pause one)
 *   timeline     last 24h: sent / failed, hourly chart, recent jobs
 *   queue        sent-email log (filters: status, opened, limit)
 *   enrollments  all enrollments (filters: status, sequence, limit) — pause / resume
 *
 * Data (unchanged): getDashboardStats + getEmailHealth (overview), GET /api/email/pgboss/status,
 * /api/email/scheduled?limit=100&hours=48, /api/email/timeline?hours=24&limit=50,
 * getQueueJobs(?status&opened&limit), getAllEnrollments(?status&sequence_id&limit),
 * getEmailSequences (filter list), pauseEnrollment / resumeEnrollment (after withConfirmAlert).
 * Overview + pg-boss status refresh every 30s.
 *
 * Only the UI changed in the redesign (Ant + ui/Metrics). Kept as-is: the 30s refresh only
 * reloads "scheduled" if that was the view when the tab first opened (stale closure).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  WarningOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  RollbackOutlined,
  ExclamationOutlined,
} from '@ant-design/icons';
import * as actionsCreators from './../../../actions/index.js';
import {
  DataTable,
  EmptyState,
  Button,
  Select,
  Segmented,
  Tooltip,
  StatusBadge,
  Spin,
  StatCard,
  Metric,
  MetricRow,
  Meter,
  StatusDot,
} from '../../../ui';

import classes from './Dashboard.css';

// Helper to safely get nested properties
const get = (obj, path, defaultValue) => {
  if (!obj) return defaultValue;
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result == null) return defaultValue;
    result = result[key];
  }
  return result != null ? result : defaultValue;
};

// "in 3 days" style (future)
const formatTimeUntil = (date) => {
  if (!date) return 'N/A';
  const diff = new Date(date) - new Date();
  if (diff < 0) return 'עכשיו';
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return days + ' ימים';
  if (hours > 0) return hours + ' שעות';
  if (minutes > 0) return minutes + ' דקות';
  return 'פחות מדקה';
};

// "3 hours ago" style (past)
const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  const diff = new Date() - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return 'לפני ' + days + ' ימים';
  if (hours > 0) return 'לפני ' + hours + ' שעות';
  if (minutes > 0) return 'לפני ' + minutes + ' דקות';
  return 'עכשיו';
};

const GREEN = '#16A34A';
const AMBER = '#D97706';
const RED = '#DC2626';
const BLUE = '#2563EB';
const GRAY = '#A1A1AA';

const JOB_STATUS = {
  sent: { label: 'נשלח', color: GREEN, icon: <CheckOutlined /> },
  failed: { label: 'נכשל', color: RED, icon: <CloseOutlined /> },
  bounced: { label: 'חזר', color: AMBER, icon: <RollbackOutlined /> },
  complaint: { label: 'תלונה', color: RED, icon: <ExclamationOutlined /> },
};
const ENROLL_STATUS = {
  active: { label: 'פעיל', color: GREEN },
  paused: { label: 'מושהה', color: AMBER },
  completed: { label: 'הושלם', color: '#0EA5E9' },
  cancelled: { label: 'בוטל', color: GRAY },
  failed: { label: 'נכשל', color: RED },
};
const LIMITS = [
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' },
  { value: 500, label: '500' },
  { value: 1000, label: '1,000' },
  { value: 0, label: 'הכל' },
];

const customerName = (c) => (c && c.firstname ? `${c.firstname} ${c.lastname || ''}`.trim() : 'N/A');

const Dashboard = (props) => {
  const [stats, setStats] = useState(null);
  const [pgBossStatus, setPgBossStatus] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [queueJobs, setQueueJobs] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openedFilter, setOpenedFilter] = useState('all');
  const [sequenceFilter, setSequenceFilter] = useState('all');
  const [limit, setLimit] = useState(50);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [listLoading, setListLoading] = useState(false); // UI only: spinner for queue/enrollments tables

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, healthData] = await Promise.all([props.getDashboardStats(), props.getEmailHealth()]);
      setStats(statsData);
      setHealth(healthData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
    setLoading(false);
  }, [props]);

  const loadPgBossStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/email/pgboss/status');
      const data = await response.json();
      setPgBossStatus(data);
    } catch (error) {
      console.error('Error loading pg-boss status:', error);
    }
  }, []);

  const loadScheduled = useCallback(async () => {
    try {
      const response = await fetch('/api/email/scheduled?limit=100&hours=48');
      const data = await response.json();
      setScheduled(data);
    } catch (error) {
      console.error('Error loading scheduled emails:', error);
    }
  }, []);

  const loadTimeline = useCallback(async () => {
    try {
      const response = await fetch('/api/email/timeline?hours=24&limit=50');
      const data = await response.json();
      setTimeline(data);
    } catch (error) {
      console.error('Error loading timeline:', error);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadPgBossStatus();
    loadScheduled();
    loadTimeline();
    loadSequences();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData();
      loadPgBossStatus();
      if (activeView === 'scheduled') loadScheduled();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeView === 'queue') {
      loadQueueJobs();
    } else if (activeView === 'enrollments') {
      loadEnrollments();
    } else if (activeView === 'scheduled') {
      loadScheduled();
    } else if (activeView === 'timeline') {
      loadTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, statusFilter, openedFilter, sequenceFilter, limit]);

  const loadSequences = async () => {
    try {
      const seqs = await props.getEmailSequences();
      setSequences(seqs);
    } catch (error) {
      console.error('Error loading sequences:', error);
    }
  };

  const loadQueueJobs = async () => {
    setListLoading(true);
    try {
      var params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (openedFilter !== 'all') params.append('opened', openedFilter);
      params.append('limit', limit);
      var jobs = await props.getQueueJobs('?' + params.toString());
      setQueueJobs(jobs);
    } catch (error) {
      console.error('Error loading queue jobs:', error);
      props.showAlert('שגיאה בטעינת תור העבודות');
    }
    setListLoading(false);
  };

  const loadEnrollments = async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sequenceFilter !== 'all') params.append('sequence_id', sequenceFilter);
      params.append('limit', limit);
      const filters = params.toString() ? '?' + params.toString() : '';
      const enrolls = await props.getAllEnrollments(filters);
      setEnrollments(enrolls);
    } catch (error) {
      console.error('Error loading enrollments:', error);
      props.showAlert('שגיאה בטעינת רישומים');
    }
    setListLoading(false);
  };

  const onPauseEnrollment = async (enrollmentId) => {
    props.withConfirmAlert(async () => {
      try {
        await props.pauseEnrollment(enrollmentId);
        props.showAlert('הרישום הושהה בהצלחה');
        loadEnrollments();
        loadScheduled();
      } catch (error) {
        console.error('Error pausing enrollment:', error);
        props.showAlert('שגיאה בהשהיית הרישום');
      }
    }, 'להשהות את שליחת הרצף ללקוח זה?');
  };

  const onResumeEnrollment = async (enrollmentId) => {
    props.withConfirmAlert(async () => {
      try {
        await props.resumeEnrollment(enrollmentId);
        props.showAlert('הרישום חודש בהצלחה');
        loadEnrollments();
        loadScheduled();
      } catch (error) {
        console.error('Error resuming enrollment:', error);
        props.showAlert('שגיאה בחידוש הרישום');
      }
    }, 'לחדש את שליחת הרצף ללקוח זה?');
  };

  // failure rate → color
  const healthColor = (rate) => (rate > 25 ? RED : rate > 10 ? AMBER : GREEN);
  const queueHealthColor = (h) => (h === 'critical' ? RED : h === 'warning' ? AMBER : h === 'healthy' ? GREEN : GRAY);

  const loadingBlock = (
    <div className={classes.loading}>
      <Spin />
    </div>
  );

  // ---- Overview ------------------------------------------------------------------
  const renderOverview = () => {
    if (!stats || !health) return loadingBlock;

    const schedulerRunning = get(stats, 'scheduler.isRunning', false);
    const pgBossRunning = get(stats, 'pgBoss.running', false);
    const queueHealth = pgBossStatus ? pgBossStatus.queueHealth : null;
    const failureRate = Number(health.failureRate) || 0;
    const minute = { sent: get(stats, 'rateLimits.minute.sent', 0), limit: get(stats, 'rateLimits.minute.limit', 14) };
    const hour = { sent: get(stats, 'rateLimits.hour.sent', 0), limit: get(stats, 'rateLimits.hour.limit', 100) };
    const day = { sent: get(stats, 'rateLimits.day.sent', 0), limit: get(stats, 'rateLimits.day.limit', 1000) };

    return (
      <div className={classes.view}>
        <div className={classes.services}>
          <StatusDot color={schedulerRunning ? GREEN : RED}>מתזמן: {schedulerRunning ? 'פעיל' : 'מושבת'}</StatusDot>
          <StatusDot color={pgBossRunning ? GREEN : RED}>pg-boss: {pgBossRunning ? 'פעיל' : 'מושבת'}</StatusDot>
          <StatusDot color={queueHealthColor(queueHealth)}>בריאות תור: {queueHealth || 'לא ידוע'}</StatusDot>
        </div>

        <div className={classes.cards}>
          <StatCard title="תור עבודות">
            <Metric size="lg" value={get(stats, 'queue.created', 0)} label="ממתינות לשליחה" />
            <MetricRow>
              <Metric value={get(stats, 'queue.active', 0)} label="בתהליך" color={BLUE} />
              <Metric value={get(stats, 'queue.completed', 0)} label="הושלמו (24ש)" color={GREEN} />
              <Metric value={get(stats, 'queue.failed', 0)} label="נכשלו (24ש)" color={RED} />
            </MetricRow>
            {pgBossStatus && pgBossStatus.jobsByState && Object.keys(pgBossStatus.jobsByState).length > 0 ? (
              <div className={classes.chips}>
                <span className={classes.chipsLabel}>pg-boss</span>
                {Object.entries(pgBossStatus.jobsByState).map(([state, count]) => (
                  <StatusBadge key={state}>
                    {state} <span className="ui-tabular">{count}</span>
                  </StatusBadge>
                ))}
              </div>
            ) : null}
          </StatCard>

          <StatCard title="רישומים">
            <Metric size="lg" value={get(stats, 'enrollments.active', 0)} label="רישומים פעילים" />
            <MetricRow>
              <Metric value={get(stats, 'enrollments.paused', 0)} label="מושהים" color={AMBER} />
              <Metric value={get(stats, 'enrollments.completed', 0)} label="הושלמו" color="#0EA5E9" />
              <Metric value={get(stats, 'enrollments.cancelled', 0)} label="בוטלו" color={GRAY} />
            </MetricRow>
            <div className={classes.upcoming}>
              <span>
                <ClockCircleOutlined /> <strong className="ui-tabular">{get(stats, 'enrollments.dueNow', 0)}</strong> מוכנים לשליחה עכשיו
              </span>
              <span>
                <CalendarOutlined /> <strong className="ui-tabular">{get(stats, 'enrollments.upcoming', 0)}</strong> מתוזמנים להמשך
              </span>
            </div>
          </StatCard>

          <StatCard title="מגבלות קצב">
            {stats.rateLimits ? (
              <>
                {/* minute: red only when full; hour: amber from 80%; day: informational blue (as before) */}
                <Meter label="דקה" value={minute.sent} max={minute.limit} warnAt={1} />
                <Meter label="שעה" value={hour.sent} max={hour.limit} warnAt={0.8} />
                <Meter label="יום" value={day.sent} max={day.limit} color={BLUE} />
              </>
            ) : (
              <EmptyState compact title="אין נתונים" />
            )}
          </StatCard>

          <StatCard title="בריאות מערכת">
            <div className={classes.health}>
              <span className={classes.healthRing} style={{ borderColor: healthColor(failureRate) }}>
                <span className={`${classes.healthValue} ui-tabular`} style={{ color: healthColor(failureRate) }}>
                  {failureRate.toFixed(1)}%
                </span>
                <span className={classes.healthLabel}>כשלונות</span>
              </span>
              <dl className={classes.healthList}>
                <dt>סף התראה</dt>
                <dd className="ui-tabular">{health.threshold || 25}%</dd>
                <dt>חלון בדיקה</dt>
                <dd className="ui-tabular">{health.windowMinutes || 30} דקות</dd>
                <dt>סטטוס</dt>
                <dd>
                  <StatusBadge color={healthColor(failureRate)}>{health.healthy ? 'תקין' : 'בעיה'}</StatusBadge>
                </dd>
              </dl>
            </div>
          </StatCard>
        </div>

        {pgBossStatus && pgBossStatus.recentActivity && pgBossStatus.recentActivity.length > 0 ? (
          <StatCard title="פעילות אחרונה (שעה אחרונה)">
            <ul className={classes.activity}>
              {pgBossStatus.recentActivity.slice(0, 10).map((activity, idx) => (
                <li key={idx}>
                  <StatusDot color={activity.state === 'completed' ? GREEN : RED}>
                    {activity.state === 'completed' ? 'נשלח' : 'נכשל'}
                  </StatusDot>
                  <span className={classes.muted}>{formatRelativeTime(activity.completedAt)}</span>
                  {activity.error ? (
                    <Tooltip title={activity.error}>
                      <WarningOutlined className={classes.warn} />
                    </Tooltip>
                  ) : null}
                </li>
              ))}
            </ul>
          </StatCard>
        ) : null}
      </div>
    );
  };

  // ---- Scheduled -----------------------------------------------------------------
  const renderScheduled = () => {
    if (!scheduled) return loadingBlock;
    var summary = scheduled.summary || {};
    var dueNowList = scheduled.dueNow || [];
    var scheduledList = scheduled.scheduled || [];

    return (
      <div className={classes.view}>
        <div className={classes.summary}>
          <StatCard>
            <Metric size="lg" value={summary.dueNowCount || 0} label="מוכנים עכשיו" color={summary.dueNowCount ? AMBER : undefined} />
          </StatCard>
          <StatCard>
            <Metric size="lg" value={summary.scheduledCount || 0} label="מתוזמנים" />
          </StatCard>
          {summary.nextSendAt ? (
            <StatCard>
              <Metric size="lg" value={formatTimeUntil(summary.nextSendAt)} label="השליחה הבאה" />
            </StatCard>
          ) : null}
        </div>

        {dueNowList.length > 0 ? (
          <StatCard title={`מוכנים לשליחה עכשיו (${dueNowList.length})`} className={classes.dueNow}>
            <ul className={classes.list}>
              {dueNowList.slice(0, 20).map((enrollment) => (
                <li key={enrollment.id}>
                  <strong>{customerName(enrollment.customer)}</strong>
                  <StatusBadge>{(enrollment.sequence || {}).name || 'N/A'}</StatusBadge>
                  <span className={`${classes.muted} ui-tabular`}>{new Date(enrollment.nextSendAt).toLocaleString('he-IL')}</span>
                </li>
              ))}
            </ul>
          </StatCard>
        ) : null}

        <DataTable
          data={scheduledList}
          fit
          empty={<EmptyState title="אין אימיילים מתוזמנים" />}
          columns={[
            {
              key: 'customer',
              title: 'לקוח',
              render: (_, e) => (
                <div className={classes.person}>
                  <strong>{customerName(e.customer)}</strong>
                  <span dir="ltr">{(e.customer || {}).mail}</span>
                </div>
              ),
            },
            {
              key: 'sequence',
              title: 'רצף',
              responsive: ['md'],
              render: (_, e) => (
                <span className={classes.seqCell}>
                  <StatusBadge>{(e.sequence || {}).name || 'N/A'}</StatusBadge>
                  <span className={classes.muted}>צעד {e.currentStep || 1}</span>
                </span>
              ),
            },
            {
              key: 'template',
              title: 'המייל הבא',
              responsive: ['lg'],
              ellipsis: true,
              render: (_, e) => (e.nextTemplate && e.nextTemplate.subject) || <span className={classes.muted}>—</span>,
            },
            {
              key: 'when',
              title: 'מתי',
              width: 170,
              render: (_, e) => (
                <div className={classes.person}>
                  <strong>{formatTimeUntil(e.nextSendAt)}</strong>
                  <span className="ui-tabular">{new Date(e.nextSendAt).toLocaleString('he-IL')}</span>
                </div>
              ),
            },
            {
              key: 'actions',
              title: '',
              width: 90,
              align: 'left',
              render: (_, e) => (
                <Button size="small" icon={<PauseCircleOutlined />} onClick={() => onPauseEnrollment(e.id)}>
                  השהה
                </Button>
              ),
            },
          ]}
        />
      </div>
    );
  };

  // ---- Timeline (last 24h) -------------------------------------------------------
  const renderTimeline = () => {
    if (!timeline) return loadingBlock;
    var summary = timeline.summary || {};
    var hourlyStats = timeline.hourlyStats || [];
    var jobs = timeline.jobs || [];
    const maxTotal = Math.max(1, ...hourlyStats.map((s) => (s.sent || 0) + (s.failed || 0) + (s.bounced || 0)));

    return (
      <div className={classes.view}>
        <div className={classes.summary}>
          <StatCard>
            <Metric size="lg" value={summary.sent || 0} label="נשלחו בהצלחה" color={GREEN} />
          </StatCard>
          <StatCard>
            <Metric size="lg" value={summary.failed || 0} label="נכשלו" color={summary.failed ? RED : undefined} />
          </StatCard>
          <StatCard>
            <Metric size="lg" value={summary.total || 0} label='סה"כ (24 שעות)' />
          </StatCard>
        </div>

        {hourlyStats.length > 0 ? (
          <StatCard title="פעילות לפי שעה">
            <div className={classes.chart} role="img" aria-label="מיילים לפי שעה">
              {hourlyStats.map((stat, idx) => {
                const sent = stat.sent || 0;
                const failed = stat.failed || 0;
                return (
                  <Tooltip key={idx} title={`${stat.hour.slice(11, 13)}:00 · נשלחו ${sent} · נכשלו ${failed}`}>
                    <div className={classes.chartCol}>
                      <div className={classes.chartBar}>
                        <span className={classes.chartFailed} style={{ height: `${(failed / maxTotal) * 100}%` }} />
                        <span className={classes.chartSent} style={{ height: `${(sent / maxTotal) * 100}%` }} />
                      </div>
                      <span className={classes.chartLabel}>{stat.hour.slice(11, 13)}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </StatCard>
        ) : null}

        <StatCard title="פעילות אחרונה">
          {jobs.length > 0 ? (
            <ul className={classes.jobs}>
              {jobs.map((job) => {
                const st = JOB_STATUS[job.status] || { label: job.status, color: GRAY, icon: <ExclamationOutlined /> };
                return (
                  <li key={job.id}>
                    <span className={classes.jobIcon} style={{ color: st.color, background: `${st.color}1A` }}>
                      {st.icon}
                    </span>
                    <span className={classes.person}>
                      <strong>{customerName(job.customer)}</strong>
                      <span className={classes.ellipsis}>{job.subject}</span>
                    </span>
                    <span className={classes.jobMeta}>
                      <StatusBadge>{(job.sequence || {}).name || 'ידני'}</StatusBadge>
                      <span className={classes.muted}>{formatRelativeTime(job.sentAt)}</span>
                    </span>
                    {job.errorMessage ? (
                      <Tooltip title={job.errorMessage}>
                        <WarningOutlined className={classes.warn} />
                      </Tooltip>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState compact title="אין פעילות ב-24 השעות האחרונות" />
          )}
        </StatCard>
      </div>
    );
  };

  // ---- Queue (sent-email log) ----------------------------------------------------
  const renderQueueJobs = () => (
    <DataTable
      data={queueJobs}
      loading={listLoading}
      empty={<EmptyState title="אין עבודות בתור" />}
      columns={[
        { key: 'customer', title: 'לקוח', render: (_, j) => <strong>{customerName(j.customer)}</strong> },
        {
          key: 'email',
          title: 'אימייל נמען',
          responsive: ['md'],
          render: (_, j) => (
            <span dir="ltr" className={classes.muted}>
              {j.recipientEmail || (j.customer || {}).mail || ''}
            </span>
          ),
        },
        { key: 'subject', title: 'נושא', dataIndex: 'subject', ellipsis: true, responsive: ['lg'] },
        {
          key: 'sequence',
          title: 'רצף',
          responsive: ['md'],
          render: (_, j) => (j.sequence ? <StatusBadge>{j.sequence.name}</StatusBadge> : <span className={classes.muted}>ידני</span>),
        },
        {
          key: 'opened',
          title: 'נפתח',
          width: 90,
          render: (_, j) => {
            const t = j.openTracking;
            if (!(t && t.open_count > 0)) return <span className={classes.muted}>—</span>;
            return (
              <Tooltip
                title={`נפתח ${t.open_count} פעמים${t.first_opened_at ? ` · פתיחה ראשונה: ${new Date(t.first_opened_at).toLocaleString('he-IL')}` : ''}`}
              >
                <StatusBadge color={GREEN}>{t.open_count}×</StatusBadge>
              </Tooltip>
            );
          },
        },
        {
          key: 'status',
          title: 'סטטוס',
          width: 110,
          render: (_, j) => {
            const st = JOB_STATUS[j.status] || { label: j.status, color: GRAY };
            const badge = <StatusBadge color={st.color}>{st.label}</StatusBadge>;
            return j.errorMessage ? (
              <Tooltip title={j.errorMessage}>
                <span className={classes.withWarn}>
                  {badge} <WarningOutlined className={classes.warn} />
                </span>
              </Tooltip>
            ) : (
              badge
            );
          },
        },
        {
          key: 'sentAt',
          title: 'נשלח',
          width: 150,
          responsive: ['sm'],
          render: (_, j) => <span className="ui-tabular">{j.sentAt ? new Date(j.sentAt).toLocaleString('he-IL') : 'N/A'}</span>,
        },
      ]}
    />
  );

  // ---- Enrollments ---------------------------------------------------------------
  const renderEnrollments = () => (
    <DataTable
      data={enrollments}
      loading={listLoading}
      empty={<EmptyState title="אין רישומים" />}
      columns={[
        { key: 'id', title: 'ID', dataIndex: 'id', width: 70, responsive: ['lg'], render: (v) => <span className={`${classes.muted} ui-tabular`}>{v}</span> },
        {
          key: 'customer',
          title: 'לקוח',
          render: (_, e) => (
            <div className={classes.person}>
              <strong>{customerName(e.customer)}</strong>
              <span dir="ltr">{(e.customer || {}).mail || ''}</span>
            </div>
          ),
        },
        { key: 'sequence', title: 'רצף', responsive: ['md'], render: (_, e) => (e.sequence || {}).name || 'N/A' },
        {
          key: 'status',
          title: 'סטטוס',
          width: 100,
          render: (_, e) => {
            const st = ENROLL_STATUS[e.status] || { label: e.status, color: GRAY };
            return <StatusBadge color={st.color}>{st.label}</StatusBadge>;
          },
        },
        { key: 'step', title: 'צעד', width: 70, responsive: ['sm'], render: (_, e) => <span className="ui-tabular">{e.currentStep || 1}</span> },
        {
          key: 'nextSend',
          title: 'שליחה הבאה',
          width: 170,
          responsive: ['md'],
          render: (_, e) => {
            if (!e.nextSendAt) return <span className={classes.muted}>—</span>;
            const isDue = new Date(e.nextSendAt) <= new Date();
            return (
              <div className={classes.person}>
                <strong style={isDue ? { color: RED } : undefined}>{formatTimeUntil(e.nextSendAt)}</strong>
                <span className="ui-tabular">{new Date(e.nextSendAt).toLocaleString('he-IL')}</span>
              </div>
            );
          },
        },
        {
          key: 'actions',
          title: '',
          width: 100,
          align: 'left',
          render: (_, e) =>
            e.status === 'active' ? (
              <Button size="small" icon={<PauseCircleOutlined />} onClick={() => onPauseEnrollment(e.id)}>
                השהיה
              </Button>
            ) : e.status === 'paused' ? (
              <Button size="small" icon={<PlayCircleOutlined />} onClick={() => onResumeEnrollment(e.id)}>
                חידוש
              </Button>
            ) : null,
        },
      ]}
    />
  );

  var dueNowCount = get(stats, 'enrollments.dueNow', 0);

  const switchView = (view) => {
    // queue/enrollments share the status filter — reset it when moving between them (as before)
    if ((view === 'queue' || view === 'enrollments') && activeView !== view) {
      setStatusFilter('all');
      setOpenedFilter('all');
    }
    setActiveView(view);
  };

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <Segmented
          value={activeView}
          onChange={switchView}
          options={[
            { value: 'overview', label: 'סקירה כללית' },
            {
              value: 'scheduled',
              label: (
                <span className={classes.segLabel}>
                  מתוזמנים {dueNowCount > 0 ? <span className={classes.count}>{dueNowCount}</span> : null}
                </span>
              ),
            },
            { value: 'timeline', label: 'היסטוריה' },
            { value: 'queue', label: 'תור עבודות' },
            { value: 'enrollments', label: 'רישומים' },
          ]}
        />

        <div className={classes.toolbarEnd}>
          {activeView === 'queue' || activeView === 'enrollments' ? (
            <>
              <Select
                className={classes.filter}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'כל הסטטוסים' },
                  ...(activeView === 'queue'
                    ? [
                        { value: 'sent', label: 'נשלחו' },
                        { value: 'failed', label: 'נכשלו' },
                        { value: 'bounced', label: 'חזרו' },
                        { value: 'complaint', label: 'תלונות' },
                      ]
                    : [
                        { value: 'active', label: 'פעילים' },
                        { value: 'paused', label: 'מושהים' },
                        { value: 'completed', label: 'הושלמו' },
                        { value: 'cancelled', label: 'בוטלו' },
                        { value: 'failed', label: 'נכשלו' },
                      ]),
                ]}
              />
              {activeView === 'queue' ? (
                <Select
                  className={classes.filter}
                  value={openedFilter}
                  onChange={setOpenedFilter}
                  options={[
                    { value: 'all', label: 'נפתח / לא נפתח' },
                    { value: 'yes', label: 'נפתחו בלבד' },
                    { value: 'no', label: 'לא נפתחו' },
                  ]}
                />
              ) : (
                <Select
                  className={classes.filter}
                  value={sequenceFilter}
                  onChange={setSequenceFilter}
                  options={[{ value: 'all', label: 'כל הרצפים' }, ...sequences.map((s) => ({ value: String(s.id), label: s.name }))]}
                />
              )}
              <Select className={classes.limit} value={limit} onChange={setLimit} options={LIMITS} />
              <Tooltip title="רענן">
                <Button icon={<ReloadOutlined />} onClick={activeView === 'queue' ? loadQueueJobs : loadEnrollments} loading={listLoading} />
              </Tooltip>
            </>
          ) : null}

          {activeView === 'overview' ? (
            <>
              {lastUpdate ? <span className={classes.lastUpdate}>עודכן {lastUpdate.toLocaleTimeString('he-IL')}</span> : null}
              <Button icon={<ReloadOutlined />} onClick={loadDashboardData} loading={loading}>
                רענן
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {activeView === 'overview' && renderOverview()}
      {activeView === 'scheduled' && renderScheduled()}
      {activeView === 'timeline' && renderTimeline()}
      {activeView === 'queue' && renderQueueJobs()}
      {activeView === 'enrollments' && renderEnrollments()}
    </div>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getDashboardStats: actionsCreators.getDashboardStats,
      getEmailHealth: actionsCreators.getEmailHealth,
      getQueueJobs: actionsCreators.getQueueJobs,
      getAllEnrollments: actionsCreators.getAllEnrollments,
      getEmailSequences: actionsCreators.getEmailSequences,
      cancelQueueJob: actionsCreators.cancelQueueJob,
      pauseEnrollment: actionsCreators.pauseEnrollment,
      resumeEnrollment: actionsCreators.resumeEnrollment,
      showAlert: actionsCreators.showAlert,
      withConfirmAlert: actionsCreators.withConfirmAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
